import React, { useState, useEffect, useRef } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { POWERBI_CONFIG } from '../config';
import './PowerBIReport.css';

function PowerBIReport({ reportId, reportName, embedUrl, datasetId }) {
  const { user } = useAuth();
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchEmbedToken();
  }, [reportId]);

  const fetchEmbedToken = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate user email is available
      if (!user || !user.email) {
        setError("User email is required to generate embed token. Please log in again.");
        setLoading(false);
        return;
      }

      // Ensure we have a valid username for RLS
      const username = user.email || user.username || POWERBI_CONFIG.defaultUserIdentity.username;
      const roles = user.roles || POWERBI_CONFIG.defaultUserIdentity.roles || [];

      const requestBody = {
        reportId,
        datasetId,
        bypassRLS: POWERBI_CONFIG.bypassRLS,
        userIdentity: {
          username: username, // Use email as username for RLS
          roles: roles // Use roles if available, otherwise empty array
        }
      };

      console.log('🔄 Fetching embed token with:', {
        reportId,
        datasetId,
        username: username,
        roles: roles,
        bypassRLS: POWERBI_CONFIG.bypassRLS,
        userObject: user,
        requestBody: requestBody
      });

      const response = await axios.post('https://sgoolfoto-backend.onrender.com/api/embed-token', requestBody);

      console.log('✅ Backend response:', {
        success: response.data.success,
        hasEmbedToken: !!response.data.embedToken,
        tokenLength: response.data.embedToken?.length,
        embedUrl: response.data.embedUrl,
        rlsBypassed: response.data.rlsBypassed
      });

      if (response.data.success) {
        const config = {
          type: 'report',
          id: reportId,
          embedUrl: embedUrl,
          accessToken: response.data.embedToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: {
                expanded: false,
                visible: true
              },
              pageNavigation: {
                visible: true
              }
            },
            background: models.BackgroundType.Transparent,
            layoutType: models.LayoutType.Custom,
            customLayout: {
              displayOption: models.DisplayOption.FitToPage
            }
          }
        };

        console.log('📊 PowerBI Embed Config:', {
          type: config.type,
          id: config.id,
          embedUrl: config.embedUrl,
          hasAccessToken: !!config.accessToken,
          tokenType: config.tokenType
        });

        setEmbedConfig(config);
        
        // Log RLS status for debugging
        if (response.data.rlsBypassed) {
          console.warn('⚠️ RLS BYPASSED - Everyone sees all data!');
        } else {
          console.log('✅ RLS Active - User:', user.email, 'Roles:', user.roles);
        }
      } else {
        setError('Failed to get embed token');
      }
    } catch (err) {
      console.error('❌ ERROR FETCHING EMBED TOKEN:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleReportLoad = () => {
    console.log('Report loaded successfully');
  };

  const handleReportRender = () => {
    console.log('Report rendered successfully');
  };

  const handleReportError = (event) => {
    console.error('❌ REPORT ERROR DETAILS:', {
      message: event.detail?.message,
      detailedMessage: event.detail?.detailedMessage,
      errorCode: event.detail?.errorCode,
      level: event.detail?.level,
      technicalDetails: event.detail
    });
    setError(`Report Error: ${event.detail?.message || 'Unknown error'}`);
  };

  const handleFullscreen = () => {
    if (reportRef.current) {
      reportRef.current.fullscreen();
    }
  };

  const handleRefresh = () => {
    fetchEmbedToken();
  };

  return (
    <div className="powerbi-report-container">
      <div className="report-header">
        <div className="report-title">
          <h2>{reportName}</h2>
          <span className="report-badge">Live Report</span>
        </div>
        <div className="report-actions">
          <button
            onClick={handleRefresh}
            className="action-button"
            title="Refresh Report"
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <button
            onClick={handleFullscreen}
            className="action-button"
            title="Fullscreen"
            disabled={loading || error}
          >
            ⛶ Fullscreen
          </button>
        </div>
      </div>

      <div className="report-content">
        {loading && (
          <div className="report-loading">
            <div className="spinner"></div>
            <p>Loading report...</p>
          </div>
        )}

        {error && (
          <div className="report-error">
            <h3>⚠️ Failed to Load Report</h3>
            <p>{error}</p>
            <button onClick={handleRefresh} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && embedConfig && (
          <PowerBIEmbed
            embedConfig={embedConfig}
            eventHandlers={
              new Map([
                ['loaded', handleReportLoad],
                ['rendered', handleReportRender],
                ['error', handleReportError]
              ])
            }
            cssClassName="report-iframe"
            getEmbeddedComponent={(embeddedReport) => {
              reportRef.current = embeddedReport;
            }}
          />
        )}
      </div>

      <div className="report-footer">
        <p>🔒 Secure embedding with Service Principal</p>
        <p className="token-info">Token auto-refreshes before expiration</p>
      </div>
    </div>
  );
}

export default PowerBIReport;

